import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ArrowLeft, Plus, Pencil } from "lucide-react";

/** Full products list with search + pagination — jumps into inline edit. */
export default function CmsProductsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/food-items", { params: { limit: 60, search: q || undefined } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-6xl" data-testid="cms-products-list">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Products</h1>
          <p className="mt-1 text-sm text-slate-500">Edit category, nutrition, health benefits, tags, deals, recalls, articles — {total} in catalog.</p>
        </div>
        <Link to="/admin/cms/products/new" data-testid="cms-product-new">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="mr-1 h-4 w-4" /> New product
          </Button>
        </Link>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="cms-product-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, GTIN, category…"
            className="pl-8"
          />
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="cms-product-search-btn">
          {loading ? "Searching…" : "Search"}
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-500">
              <th className="px-3 py-2 font-medium">Product</th>
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="px-3 py-2 font-medium">Featured</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.slug} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {it.images?.[0] && <img src={it.images[0]} alt="" className="h-8 w-8 rounded object-cover" />}
                    <div>
                      <div className="font-medium text-slate-900">{it.common_name}</div>
                      <div className="text-xs text-slate-500">{it.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-700">{it.category || it.category_slug || "—"}</td>
                <td className="px-3 py-2">{it.featured ? "★" : ""}</td>
                <td className="px-3 py-2 text-right">
                  <Link
                    to={`/admin/cms/products/${encodeURIComponent(it.slug)}/edit`}
                    data-testid={`cms-product-edit-${it.slug}`}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </Link>
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={4} className="p-6 text-center text-slate-500">No products found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
